import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { identify as klaviyoIdentify } from './klaviyo';
import { matterIdentify } from './matter-identify';
import * as gtm from './gtm';
import * as rudder from './rudderstack';
import { finishOrder, sendOrderComplete } from './order';
import { priceToCents } from './price';
import { getDigitalWalletExpressPaymentMethod, sanitizeData } from './checkout-form';
import { reportError } from './report-error';
import { buildDisplayItems, createPaymentIntent, getPaypalOptions } from './checkout-payment';
import fetcher from './fetcher';
import {
  type CheckoutFunnelData,
  type SalesFunnelData,
  useFunnelData,
} from './funnel-data-context';
import { getCountryCodeFromCurrency } from './country';
import { getCurrencyFromCartData } from './currency';
import { OrderPaymentMethod, OrderPaymentProcessor } from 'constant';

const shippingOptions = [
  {
    id: 'free',
    label: 'Free shipping',
    detail: 'Included',
    amount: 0,
  },
];

// customerData is expected to be sanitized
export const identify = ({ customerData, apiBaseUrl, funnelId }) => {
  // @ts-ignore: window
  if (window._learnq && window._learnq.isIdentified && window._learnq.isIdentified() !== true) {
    klaviyoIdentify(customerData);
  }

  matterIdentify({ customerData, apiBaseUrl, funnelId, subscribeSms: true });
};

export const success = ({
  orderId,
  authKey,
  customerData,
  cartData,
  upsellUrl,
  apiBaseUrl,
  funnelId,
  router,
  paymentMethod,
}) => {
  identify({ customerData, apiBaseUrl, funnelId });

  const purchaseData = {
    orderId,
    cartData,
    purchaseType: gtm.PURCHASE_TYPES.MAIN_OFFER,
    customerData,
    paymentMethod,
  };
  gtm.onPurchase(purchaseData);
  rudder.onPurchase(purchaseData);

  if (!upsellUrl) {
    // No downsellUrl set, so finish and redirect to order summary.
    return finishOrder(orderId, authKey, apiBaseUrl, funnelId);
  } else {
    // Go to next step in the funnel.
    return router.push(upsellUrl);
  }
};

// - Sets up Google Pay/Apple Pay if available
// - Updates request for offer
// - Sets up event listeners for Google Pay/Apple Pay
export const usePaymentRequest = ({
  onClick,
  cartData,
  cartIsReady,
  upsellUrl,
  apiBaseUrl,
  funnelId,
  funnelName,
  adData,
  router,
  countryShippingWhitelist,
  setShouldShowActivityIndicator,
  setOrderId,
  setAuthKey,
  setPaymentProcessor,
  setPaymentMethod,
  setFormErrorMessage,
  setModalError,
  setCustomerData,
}) => {
  const stripe = useStripe();

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestPaymentMethod, setPaymentRequestPaymentMethod] = useState(null);
  const [supportsDigitalWallet, setSupportsDigitalWallet] = useState(true);

  const { ga4MeasurementId } = useFunnelData<SalesFunnelData | CheckoutFunnelData>();

  const handlePaymentRequestClick = useCallback(
    ev => {
      // Handling disabled state here (see comment below).
      // Any asynchronous logic needs to happen after you have decided whether to show the form or not
      // and have called ev.preventDefault, if appropriate. See validation for an example.
      //
      // If cart is not ready, don't show the payment interface
      if (!cartIsReady) {
        ev.preventDefault();
        return;
      }

      // Ignore click for mock data
      if (cartData.data.mock) {
        ev.preventDefault();
        return;
      }

      setShouldShowActivityIndicator(true);
      setFormErrorMessage(null);
      onClick(paymentRequestPaymentMethod);
    },
    [
      cartIsReady,
      cartData?.data.mock,
      onClick,
      paymentRequestPaymentMethod,
      setFormErrorMessage,
      setShouldShowActivityIndicator,
    ]
  );

  // Setup Google Pay/Apple Pay if available
  useEffect(() => {
    async function setupPaymentRequest() {
      if (!stripe || !cartIsReady || !!paymentRequest) return;

      const currency = getCurrencyFromCartData(cartData);

      const pr = stripe.paymentRequest({
        country: getCountryCodeFromCurrency(currency),
        currency,
        displayItems: buildDisplayItems(cartData),
        total: {
          label: 'Total',
          amount: Number(priceToCents(cartData?.data.total)),
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
        requestShipping: true,
        shippingOptions,
      });

      // Check the availability of the Payment Request API.
      const result = await pr.canMakePayment();
      if (result) {
        setPaymentRequest(pr);

        // Detect the type of payment request button now so it can be tracked if
        // the button is clicked.
        if (result?.applePay) {
          setPaymentRequestPaymentMethod(OrderPaymentMethod.APPLE_PAY_EXPRESS);
        } else if (result?.googlePay) {
          setPaymentRequestPaymentMethod(OrderPaymentMethod.GOOGLE_PAY_EXPRESS);
        } else if (result) {
          setPaymentRequestPaymentMethod(OrderPaymentMethod.CREDIT_CARD_EXPRESS);
        }
      } else {
        setSupportsDigitalWallet(false);
      }
    }

    setupPaymentRequest();
  }, [
    stripe,
    cartData,
    cartIsReady,
    paymentRequest,
    setPaymentRequest,
    setPaymentRequestPaymentMethod,
    setSupportsDigitalWallet,
  ]);

  useEffect(() => {
    async function updatePaymentRequest() {
      if (!paymentRequest || !cartIsReady) return;
      paymentRequest.update({
        currency: cartData?.data.currency?.toLowerCase(),
        displayItems: buildDisplayItems(cartData),
        total: {
          label: 'Total',
          amount: Number(priceToCents(cartData?.data.total)),
        },
      });
    }

    updatePaymentRequest();
  }, [stripe, cartData, paymentRequest, cartIsReady]);

  // Event listeners for Google Pay/Apple Pay
  useEffect(() => {
    if (!paymentRequest) return;

    let didCancel = false;
    let handlingPaymentMethod = false;

    paymentRequest.on('paymentmethod', async ev => {
      handlingPaymentMethod = true;
      const paymentMethod = getDigitalWalletExpressPaymentMethod(ev.walletName);

      const triggerSuccess = (orderId, authKey, customerData) => {
        handlingPaymentMethod = false;
        success({
          orderId,
          authKey,
          customerData,
          paymentMethod,
          cartData,
          upsellUrl,
          apiBaseUrl,
          funnelId,
          router,
        });
      };

      const fail = (error, shouldComplete = true) => {
        handlingPaymentMethod = false;
        setShouldShowActivityIndicator(false);
        setOrderId(null);
        setAuthKey(null);
        setPaymentProcessor(null);
        setPaymentMethod(null);
        setFormErrorMessage(error?.message);
        // Report to the browser that the payment failed, prompting it to
        // re-show the payment interface, or show an error message and close
        // the payment interface.
        if (shouldComplete) ev.complete('fail');
      };

      // Fails the payment and allows the user to retry unless paymentIntent
      // is specified which results in a blocking modal error and the end of the funnel.
      const handleCancel = async (paymentIntent?: any, orderId?: any, authKey?: any) => {
        didCancel = false;
        handlingPaymentMethod = false;

        // If no paymentIntent was specfied, just fail and allow user to retry.
        if (!paymentIntent) return fail('No paymentIntent was specfied');

        setOrderId(null);
        setAuthKey(null);
        setPaymentProcessor(null);
        setPaymentMethod(null);

        // The payment was completed before the cancel event occurred. Show blocking modal to end the funnel.
        setModalError({
          message:
            'Your payment was already completed. Please contact our support team to cancel your order.',
          shouldComplete: false,
          orderId,
          authKey,
        });

        try {
          // Complete the order immediately so receipt goes out before 30 minute wait time.
          await sendOrderComplete(orderId, apiBaseUrl);
        } catch (err) {
          console.error(err);
          reportError(err);
        }

        // Close the payment interface.
        ev.complete('fail');
      };

      if (didCancel) return handleCancel();

      const { payerEmail: email, payerName, payerPhone, shippingAddress: address } = ev;

      const {
        addressLine,
        city: shippingCity,
        country: shippingCountry,
        region: shippingState,
        postalCode: shippingZip,
      } = address;

      // Strip all non-digits
      const sanitizedPhone = payerPhone.replace(/\D/g, '');

      let phone;
      // Only use the phone number if there is at least 10 digits
      if (sanitizedPhone.length >= 10) {
        phone = sanitizedPhone;

        // prefix the country-code (assume North America)
        if (phone.length === 10) {
          phone = `1${phone}`;
        }

        // prefix the + symbol
        if (phone.length === 11) {
          phone = `+${phone}`;
        }
      }

      const nameParts = payerName.split(' ').filter(part => !!part);

      // Use the last word as the last name, even if there is only a single word.
      const lastName = nameParts.pop();

      // Assume first name is everything else, or undefined if name was only a single word.
      // Has to be set to undefined, or FB capi will try to sanitize and error
      const firstName = nameParts.length ? nameParts.join(' ') : undefined;

      const shippingAddress = addressLine.filter(line => !!line).join(' ');

      // Get the velocity_lead_id from Rudderstack if it's available
      const velocityLeadId = rudder.getLeadId();

      const customerData = sanitizeData({
        email,
        firstName,
        lastName,
        shippingAddress,
        shippingCity,
        shippingCountry,
        shippingState,
        shippingZip,
        phone,
        velocityLeadId,
      });

      const {
        clientSecret,
        orderId,
        key,
        error: piError,
      } = await createPaymentIntent({
        paymentProcessor: OrderPaymentProcessor.STRIPE,
        paymentMethod,
        apiBaseUrl,
        cartData,
        customerData,
        funnelId,
        adData,
        funnelName,
        ga4MeasurementId,
      });

      if (piError) return fail(piError);

      setCustomerData(customerData);
      setOrderId(orderId);
      setAuthKey(key);
      setPaymentProcessor(OrderPaymentProcessor.STRIPE);
      setPaymentMethod(paymentMethod);

      if (didCancel) return handleCancel();

      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: ev.paymentMethod.id,
          shipping: {
            address: {
              line1: shippingAddress,
              city: shippingCity,
              country: shippingCountry,
              state: shippingState,
              postal_code: shippingZip,
            },
            phone: payerPhone,
            name: payerName,
          },
        },
        { handleActions: false }
      );

      if (confirmError) return fail(confirmError);

      // Report to the browser that the confirmation was successful, prompting
      // it to close the browser payment method collection interface.
      ev.complete('success');

      // Check if the PaymentIntent requires any actions and if so let Stripe.js
      // handle the flow.
      if (paymentIntent.status === 'requires_action') {
        // Let Stripe.js handle the rest of the payment flow.
        const { error } = await stripe.confirmCardPayment(clientSecret);

        if (error) {
          fail(error, false);
        } else if (didCancel) {
          handleCancel(paymentIntent, orderId, key);
        } else {
          triggerSuccess(orderId, key, customerData);
        }
      } else if (didCancel) {
        handleCancel(paymentIntent, orderId, key);
      } else {
        triggerSuccess(orderId, key, customerData);
      }
    });

    paymentRequest.on('cancel', async () => {
      if (handlingPaymentMethod) {
        // If we are currently in the 'paymentmethod' event handler cancelling will be handled there.
        didCancel = true;
      } else {
        setShouldShowActivityIndicator(false);
      }
    });

    // https://stripe.com/docs/js/payment_request/events/on_shipping_option_change
    paymentRequest.on('shippingaddresschange', ({ updateWith, shippingAddress }) => {
      const { country } = shippingAddress;
      if (!countryShippingWhitelist.includes(country)) {
        // https://stripe.com/docs/js/appendix/update_details
        updateWith({ status: 'invalid_shipping_address' });
      } else {
        updateWith({
          status: 'success',
          shippingOptions,
        });
      }
    });
    return () => {
      paymentRequest.off('paymentmethod');
      paymentRequest.off('cancel');
      paymentRequest.off('shippingaddresschange');
    };
  }, [
    paymentRequest,
    countryShippingWhitelist,
    funnelId,
    funnelName,
    ga4MeasurementId,
    cartData,
    upsellUrl,
    apiBaseUrl,
    adData,
    setAuthKey,
    setOrderId,
    setPaymentProcessor,
    setPaymentMethod,
    setCustomerData,
    stripe,
    router,
    setFormErrorMessage,
    setModalError,
    setShouldShowActivityIndicator,
  ]);

  return {
    paymentRequest,
    handlePaymentRequestClick,
    supportsDigitalWallet,
  };
};

export const usePayPalExpress = ({
  setShouldShowActivityIndicator,
  onClick,
  setOrderId,
  setAuthKey,
  setPaymentProcessor,
  setPaymentMethod,
  apiBaseUrl,
  cartData,
  cartIsReady,
  funnelId,
  adData,
  funnelName,
  setCustomerData,
  orderDataOrderId,
  orderDataAuthKey,
  upsellUrl,
  router,
  countryShippingWhitelist,
  setFormErrorMessage,
}) => {
  const payPalOnClickRef = useRef({
    onClick,
    cartIsReady,
    mock: cartData?.data.mock,
  });

  const { ga4MeasurementId } = useFunnelData();

  useEffect(() => {
    payPalOnClickRef.current = {
      onClick,
      cartIsReady,
      mock: cartData?.data.mock,
    };
  }, [onClick, cartIsReady, cartData?.data.mock]);

  const handlePayPalExpressOnClick = useCallback(
    async (_, actions) => {
      // Ignore click if the cart is not ready
      if (!payPalOnClickRef.current.cartIsReady) {
        return actions.reject();
      }

      // Ignore click for mock data
      if (payPalOnClickRef.current.mock) {
        return actions.reject();
      }

      setShouldShowActivityIndicator(true);
      payPalOnClickRef.current.onClick(OrderPaymentMethod.PAYPAL_EXPRESS);
      return actions.resolve();
    },
    // PayPal callback functions only bind once so we can't rely on React handling the binding
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const payPalCreateOrderRef = useRef({
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    apiBaseUrl,
    cartData,
    funnelId,
    adData,
    funnelName,
    ga4MeasurementId,
  });
  useEffect(() => {
    payPalCreateOrderRef.current = {
      setOrderId,
      setAuthKey,
      setPaymentProcessor,
      setPaymentMethod,
      apiBaseUrl,
      cartData,
      funnelId,
      adData,
      funnelName,
      ga4MeasurementId,
    };
  }, [
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    apiBaseUrl,
    cartData,
    funnelId,
    adData,
    funnelName,
    ga4MeasurementId,
  ]);

  const handlePayPalExpressCreateOrder = useCallback(async () => {
    const hasBundleOffers = payPalCreateOrderRef.current.cartData.data.lineItems.some(
      item => item.hasOfferItems === true
    );
    const hasMultipleItems = payPalCreateOrderRef.current.cartData.data.lineItems.length > 1;
    const useMultiItemFlow = hasMultipleItems && !hasBundleOffers;

    let result;
    if (useMultiItemFlow) {
      // Import the function
      const { handleMultipleLineItemsPayPal } = await import('./checkout-payment');

      result = await handleMultipleLineItemsPayPal({
        paymentProcessor: OrderPaymentProcessor.PAYPAL,
        paymentMethod: OrderPaymentMethod.PAYPAL_EXPRESS,
        apiBaseUrl: payPalCreateOrderRef.current.apiBaseUrl,
        cartData: payPalCreateOrderRef.current.cartData,
        customerData: undefined, // Express has no customer data initially
        funnelId: payPalCreateOrderRef.current.funnelId,
        adData: payPalCreateOrderRef.current.adData,
        funnelName: payPalCreateOrderRef.current.funnelName,
        ga4MeasurementId: payPalCreateOrderRef.current.ga4MeasurementId,
      });
    } else {
      result = await createPaymentIntent({
        paymentProcessor: OrderPaymentProcessor.PAYPAL,
        paymentMethod: OrderPaymentMethod.PAYPAL_EXPRESS,
        apiBaseUrl: payPalCreateOrderRef.current.apiBaseUrl,
        cartData: payPalCreateOrderRef.current.cartData,
        funnelId: payPalCreateOrderRef.current.funnelId,
        adData: payPalCreateOrderRef.current.adData,
        funnelName: payPalCreateOrderRef.current.funnelName,
        ga4MeasurementId: payPalCreateOrderRef.current.ga4MeasurementId,
      });
    }

    if (result.error) throw new Error(result.error.message);

    payPalCreateOrderRef.current.setOrderId(result.orderId);
    payPalCreateOrderRef.current.setAuthKey(result.key);
    payPalCreateOrderRef.current.setPaymentProcessor(OrderPaymentProcessor.PAYPAL);
    payPalCreateOrderRef.current.setPaymentMethod(OrderPaymentMethod.PAYPAL_EXPRESS);
    return result.paypalOrderId;
  }, []);

  const payPalOnApproveRef = useRef({
    setCustomerData,
    orderDataOrderId,
    orderDataAuthKey,
    cartData,
    upsellUrl,
    apiBaseUrl,
    funnelId,
    router,
  });
  useEffect(() => {
    payPalOnApproveRef.current = {
      setCustomerData,
      orderDataOrderId,
      orderDataAuthKey,
      cartData,
      upsellUrl,
      apiBaseUrl,
      funnelId,
      router,
    };
  }, [
    setCustomerData,
    orderDataOrderId,
    orderDataAuthKey,
    cartData,
    upsellUrl,
    apiBaseUrl,
    funnelId,
    router,
  ]);

  const handlePayPalExpressOnApprove = useMemo(
    () => {
      return async (data, actions) => {
        try {
          const { customerData } = await fetcher(
            `${payPalOnApproveRef.current.apiBaseUrl}/capture`,
            {
              // Needed for CORS request to send cookies
              credentials: 'include',
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                paypalOrderId: data.orderID,
              }),
            }
          );
          const sanitized = sanitizeData(customerData);

          payPalOnApproveRef.current.setCustomerData(sanitized);

          success({
            orderId: payPalOnApproveRef.current.orderDataOrderId,
            authKey: payPalOnApproveRef.current.orderDataAuthKey,
            customerData: sanitized,
            paymentMethod: OrderPaymentMethod.PAYPAL_EXPRESS,
            cartData: payPalOnApproveRef.current.cartData,
            upsellUrl: payPalOnApproveRef.current.upsellUrl,
            apiBaseUrl: payPalOnApproveRef.current.apiBaseUrl,
            funnelId: payPalOnApproveRef.current.funnelId,
            router: payPalOnApproveRef.current.router,
          });
        } catch (err) {
          if (err.statusCode === 422) {
            return actions.restart();
          }

          throw err;
        }
      };
    },
    // PayPal callback functions only bind once so we can't rely on React handling the binding
    []
  );

  const payPalOnShippingChangeRef = useRef({ countryShippingWhitelist });
  useEffect(() => {
    payPalOnShippingChangeRef.current = { countryShippingWhitelist };
  }, [countryShippingWhitelist]);
  const handlePayPalOnShippingChange = useCallback(
    (data, actions) => {
      if (
        !payPalOnShippingChangeRef.current.countryShippingWhitelist ||
        payPalOnShippingChangeRef.current.countryShippingWhitelist.includes(
          data.shipping_address.country_code
        )
      ) {
        return actions.resolve();
      }

      return actions.reject();
    },
    // PayPal callback functions only bind once so we can't rely on React handling the binding
    []
  );

  const payPalOnErrorRef = useRef({
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    setShouldShowActivityIndicator,
    setFormErrorMessage,
  });
  useEffect(() => {
    payPalOnErrorRef.current = {
      setOrderId,
      setAuthKey,
      setPaymentProcessor,
      setPaymentMethod,
      setShouldShowActivityIndicator,
      setFormErrorMessage,
    };
  }, [
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    setShouldShowActivityIndicator,
    setFormErrorMessage,
  ]);

  const handlePayPalOnError = useCallback(
    error => {
      payPalOnErrorRef.current.setShouldShowActivityIndicator(false);
      payPalOnErrorRef.current.setFormErrorMessage('Unable to process payment, please try again.');
      payPalOnErrorRef.current.setOrderId(null);
      payPalOnErrorRef.current.setAuthKey(null);
      payPalOnErrorRef.current.setPaymentProcessor(null);
      payPalOnErrorRef.current.setPaymentMethod(null);
      console.error(error);
      reportError(error);
    },
    // PayPal callback functions only bind once so we can't rely on React handling the binding
    []
  );

  const payPalOnCancelRef = useRef({
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    setShouldShowActivityIndicator,
  });
  useEffect(() => {
    payPalOnCancelRef.current = {
      setOrderId,
      setAuthKey,
      setPaymentProcessor,
      setPaymentMethod,
      setShouldShowActivityIndicator,
    };
  }, [
    setOrderId,
    setAuthKey,
    setPaymentProcessor,
    setPaymentMethod,
    setShouldShowActivityIndicator,
  ]);

  const handlePayPalOnCancel = useCallback(
    () => {
      payPalOnCancelRef.current.setShouldShowActivityIndicator(false);
      payPalOnCancelRef.current.setOrderId(null);
      payPalOnCancelRef.current.setAuthKey(null);
      payPalOnCancelRef.current.setPaymentProcessor(null);
      payPalOnCancelRef.current.setPaymentMethod(null);
    },
    // PayPal callback functions only bind once so we can't rely on React handling the binding
    []
  );

  return {
    paypalOptions: getPaypalOptions(cartData),
    handlePayPalExpressOnClick,
    handlePayPalExpressCreateOrder,
    handlePayPalExpressOnApprove,
    handlePayPalOnShippingChange,
    handlePayPalOnError,
    handlePayPalOnCancel,
  };
};
